import { MdbTooltipModule, MdbTooltipDirective } from 'mdb-angular-ui-kit/tooltip';
import { MdbPopoverModule, MdbPopoverDirective } from 'mdb-angular-ui-kit/popover';
import { MdbRippleModule } from 'mdb-angular-ui-kit/ripple';
import { NgSelectModule } from '@ng-select/ng-select';
import { TranslateModule } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormsModule , ReactiveFormsModule } from '@angular/forms';
import { Component, ElementRef, Input, ViewChild, HostListener , NO_ERRORS_SCHEMA, OnDestroy, AfterViewInit } from '@angular/core';
import { AcmicService } from '../../../_services/acmic.service';
import { TooltipService } from '@mapfre-design-library/src/lib/_services/tooltip.service';

type TooltipPlacement = 'top' | 'bottom' | 'left' | 'right';

@Component({
  standalone: true,
  schemas: [NO_ERRORS_SCHEMA],
  imports: [CommonModule, FormsModule, ReactiveFormsModule, RouterModule, TranslateModule, NgSelectModule, MdbTooltipModule, MdbPopoverModule, MdbRippleModule],
  selector: 'mapfre-tooltip',
  templateUrl: './tooltip.component.html'
})
export class TooltipComponent implements OnDestroy, AfterViewInit {
  @Input() project = 'mapfre';
  @Input() additionalClasses:       string;
  @Input() tooltipCopy:             string;
  @Input() placementLocation:       string;
  @Input() desktopPlacementLocation: string;
  @Input() link:                    string;
  @Input() title:                   string;
  @Input() fullTitle:               string;
  @Input() description:             string;
  @Input() trigger:                 string;
  @Input() autoCloseProp:           string;
  @Input() imgPath:                 string;
  @Input() imageHasBody = false;
  @Input() linkTitle = 'MA Auto Policy';
  @Input() hasCloseButton = true;
  @ViewChild('tooltip') tooltip: MdbTooltipDirective | MdbPopoverDirective;
  isAcmic = false;
  isOpen = false;
  computedPlacement: TooltipPlacement;

  get responsivePlacementLocation(): string {
    if (this.computedPlacement) {
      return this.computedPlacement;
    }
    return this.desktopPlacementLocation && typeof window !== 'undefined' && window.innerWidth >= 768
      ? this.desktopPlacementLocation
      : this.placementLocation;
  }

  constructor(  
    private acmicService: AcmicService,
    private tooltipService: TooltipService,
    private elementRef: ElementRef<HTMLElement>
  ) {
      this.isAcmic = this.acmicService.isACMIC();
    }

  // MDB only renders at the author-configured side; flip to the opposite side
  // ourselves whenever that side has less room than its opposite.
  private calculatePlacement(): TooltipPlacement {
    const preferred = ((typeof window !== 'undefined' && window.innerWidth >= 768 && this.desktopPlacementLocation) || this.placementLocation || 'top') as TooltipPlacement;

    if (typeof window === 'undefined' || !this.elementRef?.nativeElement) {
      return preferred;
    }

    const hostRect = this.elementRef.nativeElement.getBoundingClientRect();
    const spaceAbove = hostRect.top;
    const spaceBelow = window.innerHeight - hostRect.bottom;
    const spaceLeft = hostRect.left;
    const spaceRight = window.innerWidth - hostRect.right;

    if (preferred === 'top' && spaceAbove < spaceBelow) {
      return 'bottom';
    }
    if (preferred === 'bottom' && spaceBelow < spaceAbove) {
      return 'top';
    }
    if (preferred === 'left' && spaceLeft < spaceRight) {
      return 'right';
    }
    if (preferred === 'right' && spaceRight < spaceLeft) {
      return 'left';
    }
    return preferred;
  }

  @HostListener('document:click', ['$event'])
  clickOutSide(event) {
    if (this.isOpen && event && ((event.pointerType === "mouse") || ((event.pointerType === "touch") && this.imageHasBody && 
    (!(event.target).closest(".img-body-tooltip-icon")) && !(event.target).closest(".tooltip-arrow") ) ||
     (!this.hasCloseButton && (!(event.target).closest(".coverage-tooltip-icon")) && (!(event.target).closest(".tooltip-arrow"))))
      && !(event.target).closest(".tooltip-inner")) {
      this.tooltip.hide();
    }
  }

  ngAfterViewInit(): void {
    const observer = new MutationObserver(() => {
      const tooltipEl = document.querySelector('mdb-tooltip, mdb-popover') as HTMLElement;
      if (tooltipEl?.style) {
        tooltipEl.style.willChange = 'auto';
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    this.tooltipService.register(this.title, this)
  }

  ngOnDestroy(){
    this.tooltipService.unregister(this.title)
    this.cancelHide();
  }

  show(){
    this.cancelHide();
    this.computedPlacement = this.calculatePlacement();
    this.tooltip.placement = this.computedPlacement;
    this.tooltip.show()
    this.tooltipService.open(this.title)
  }

  // Hide after a short delay so moving the cursor from the icon into the tooltip
  // content (to scroll, click a link, etc.) doesn't close it first.
  private hideTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private readonly hideDelay = 250;

  hide(){
    this.cancelHide();
    this.hideTimeoutId = setTimeout(() => {
      this.hideTimeoutId = null;
      this.tooltip.hide();
    }, this.hideDelay);
  }

  cancelHide(){
    if (this.hideTimeoutId) {
      clearTimeout(this.hideTimeoutId);
      this.hideTimeoutId = null;
    }
  }

  onTooltipShow(){
    this.isOpen = true;
  }

  onTooltipHide(){
    this.isOpen = false;
  }
}
